// src/screens/DebugVocab.jsx
import React, { useState, useEffect } from "react";
import "../App.css";
import { supabase } from "../supabaseClient";
import { labelForLanguage } from "../data/languages";
import { TARGET_WORDS, PACE_YEARS } from "../data/learningConfig";


export default function DebugVocab({
  styles,
  onBack,
  language,
  pace,
  profileId,
}) {
  const langId = language || "es-de";
  const paceValue = pace || "medium";
  const profile = profileId || "default";

  const years = PACE_YEARS[paceValue] ?? PACE_YEARS.medium;
  const weeks = Math.round(years * 52);
  const weeklyWords = Math.ceil(TARGET_WORDS / weeks);

  const [loading, setLoading] = useState(true);
  const [allWeeklySets, setAllWeeklySets] = useState([]); // [{weekIndex, totalWords, words:[]}, ...]
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function loadWeeklyFromRemote() {
      setLoading(true);
      setError("");

      try {
        // Aktuellen User holen
        const { data: userData, error: userError } =
          await supabase.auth.getUser();
        if (userError || !userData?.user?.id) {
          if (!cancelled) {
            setError("Kein eingeloggter Benutzer gefunden.");
            setAllWeeklySets([]);
            setSelectedWeek(null);
          }
          return;
        }
        const userId = userData.user.id;

        // Alle Weekly-Sets für diese Konfiguration holen
        const { data, error: weeklyError } = await supabase
          .from("weekly_sets")
          .select("week_index, total_words, words")
          .eq("user_id", userId)
          .eq("profile_id", profile)
          .eq("language_id", langId)
          .eq("pace", paceValue)
          .order("week_index", { ascending: true });

        if (weeklyError) {
          console.warn("DebugVocab: weekly_sets load error:", weeklyError);
          if (!cancelled) {
            setError("Fehler beim Laden der Wochen-Vokabeln.");
            setAllWeeklySets([]);
            setSelectedWeek(null);
          }
          return;
        }

        const normalized =
          (data || []).map((row) => ({
            weekIndex: row.week_index,
            totalWords: row.total_words,
            words: Array.isArray(row.words) ? row.words : [],
          })) || [];

        if (!cancelled) {
          setAllWeeklySets(normalized);
          setError("");
          if (normalized.length > 0) {
            setSelectedWeek(normalized[0].weekIndex);
          } else {
            setSelectedWeek(null);
          }
        }
      } catch (e) {
        console.warn("DebugVocab: loadWeeklyFromRemote exception:", e);
        if (!cancelled) {
          setError("Unerwarteter Fehler beim Laden der Wochen-Vokabeln.");
          setAllWeeklySets([]);
          setSelectedWeek(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadWeeklyFromRemote();

    return () => {
      cancelled = true;
    };
  }, [langId, paceValue, profile]);

  return (
    <>
      <h1 style={styles.title}>Developer · Weekly-Vokabeln</h1>
      <p style={styles.subtitle}>
        Wochenlisten für{" "}
        <strong>{labelForLanguage(langId)}</strong>, Tempo{" "}
        <strong>{paceValue || "medium"}</strong> (Ziel: {weeklyWords}{" "}
        Wörter/Woche, Profil: <code>{profile}</code>).
      </p>

      {loading && <p>Weekly-Vokabeln werden geladen…</p>}

      {!loading && error && (
        <p style={{ color: "var(--fluent-coral-dark)", fontSize: 13 }}>{error}</p>
      )}

      {!loading && !error && allWeeklySets.length === 0 && (
        <div style={styles.infoBox}>
          <p style={{ fontSize: 13 }}>
            Für diese Konfiguration existiert aktuell{" "}
            <strong>noch kein Weekly-Set</strong>.
          </p>
          <p style={{ fontSize: 13, opacity: 0.8, marginTop: 4 }}>
            Starte einmal die Karteikarten-Übung in Woche 1–3, damit fluent! die
            jeweiligen Wochenlisten erzeugt. Anschließend tauchen sie hier auf.
          </p>
        </div>
      )}

      {!loading && !error && allWeeklySets.length > 0 && (
        <>
          <div style={styles.infoBox}>
            <p style={{ fontSize: 13, marginBottom: 6 }}>
              Gefundene Wochen:{" "}
              {allWeeklySets.map((ws) => ws.weekIndex).join(", ")}
            </p>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {allWeeklySets.map((ws) => {
                const active = ws.weekIndex === selectedWeek;
                return (
                  <button
                    key={ws.weekIndex}
                    type="button"
                    className="btn-clickable"
                    onClick={() => setSelectedWeek(ws.weekIndex)}
                    style={{
                      ...styles.secondaryButton,
                      padding: "6px 10px",
                      fontSize: 12,
                      borderColor: active ? "var(--fluent-blue)" : "var(--fluent-border)",
                      background: active ? "var(--fluent-fog)" : "var(--fluent-white)",
                    }}
                  >
                    Woche {ws.weekIndex} ({ws.words.length} Wörter)
                  </button>
                );
              })}
            </div>
          </div>

          {(() => {
            const current =
              allWeeklySets.find((ws) => ws.weekIndex === selectedWeek) ||
              allWeeklySets[0];
            const weeklySet = current?.words || [];

            return (
              <div
                style={{
                  marginTop: 12,
                  maxHeight: 320,
                  overflow: "auto",
                  borderRadius: 12,
                  border: "1px solid var(--fluent-border)",
                }}
              >
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: 13,
                  }}
                >
                  <thead
                    style={{
                      position: "sticky",
                      top: 0,
                      background: "var(--fluent-fog)",
                      zIndex: 1,
                    }}
                  >
                    <tr>
                      <th
                        style={{
                          textAlign: "left",
                          padding: "8px 10px",
                          borderBottom: "1px solid var(--fluent-border)",
                        }}
                      >
                        Woche
                      </th>
                      <th
                        style={{
                          textAlign: "left",
                          padding: "8px 10px",
                          borderBottom: "1px solid var(--fluent-border)",
                        }}
                      >
                        ID
                      </th>
                      <th
                        style={{
                          textAlign: "left",
                          padding: "8px 10px",
                          borderBottom: "1px solid var(--fluent-border)",
                        }}
                      >
                        Spanisch
                      </th>
                      <th
                        style={{
                          textAlign: "left",
                          padding: "8px 10px",
                          borderBottom: "1px solid var(--fluent-border)",
                        }}
                      >
                        Deutsch
                      </th>
                      <th
                        style={{
                          textAlign: "left",
                          padding: "8px 10px",
                          borderBottom: "1px solid var(--fluent-border)",
                        }}
                      >
                        Typ
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {weeklySet.map((w) => (
                      <tr key={w.id}>
                        <td
                          style={{
                            padding: "6px 10px",
                            borderBottom: "1px solid var(--fluent-fog)",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {current.weekIndex}
                        </td>
                        <td
                          style={{
                            padding: "6px 10px",
                            borderBottom: "1px solid var(--fluent-fog)",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {w.id}
                        </td>
                        <td
                          style={{
                            padding: "6px 10px",
                            borderBottom: "1px solid var(--fluent-fog)",
                          }}
                        >
                          {w.es}
                        </td>
                        <td
                          style={{
                            padding: "6px 10px",
                            borderBottom: "1px solid var(--fluent-fog)",
                          }}
                        >
                          {w.de}
                        </td>
                        <td
                          style={{
                            padding: "6px 10px",
                            borderBottom: "1px solid var(--fluent-fog)",
                            whiteSpace: "nowrap",
                            opacity: 0.8,
                          }}
                        >
                          {w.type || "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </>
      )}

      <button
        className="btn-clickable"
        style={{ ...styles.secondaryButton, marginTop: 16 }}
        onClick={onBack}
      >
        Zurück zum Hauptmenü
      </button>
    </>
  );
}
